import { ICaring } from "../../Models/ICaring";
import { IPatient } from "../../Models/IPatient";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { IUser } from "../../Models/IUser";
import { getSensorIconTooltip } from "../../Utils/SensorMaps";

// Regole di Setup lette dai dati che la pagina ha già (le stesse del backend)

// un campo della persona assistita è vuoto se manca o vale "-"
export const filled = (value: any) => {
  const text = value === null || value === undefined ? "" : String(value).trim();
  return text !== "" && text !== "-";
};

export const hasPerson = (patient: IPatient | undefined | null) => {
  return !!patient && filled(patient.identifier);
};

// zona · stanza · letto, solo i campi compilati
export const placeOf = (patient: IPatient | undefined | null) => {
  if (!patient) return "";
  const parts: string[] = [];
  if (filled(patient.floor)) parts.push("zona " + patient.floor);
  // la stanza "00" si omette come nelle email
  if (filled(patient.room) && String(patient.room).trim() !== "00") {
    parts.push("stanza " + patient.room);
  }
  if (filled(patient.bed)) parts.push("letto " + patient.bed);
  return parts.join(" · ");
};

export type DeviceKind = "Letto" | "Varco";

// il tipo è sentinel.identifier («Letto» o «Varco», lo scrive il server alla creazione); per i dispositivi
// con un altro nome vale la regola delle email: solo porte = Varco
export const kindOf = (sentinel: ISentinelRich): DeviceKind => {
  const id = String(sentinel.identifier ?? "");
  if (/varco/i.test(id)) return "Varco";
  if (/letto/i.test(id)) return "Letto";
  const sensors = sentinel.sensors ?? [];
  return sensors.length > 0 && sensors.every((x: ISensor) => x.type === "door") ? "Varco" : "Letto";
};

// nome che si legge: quello di «Nome e posizione», altrimenti tipo e numero
export const dispName = (s: ISentinelRich) => (hasPerson(s.patient) ? s.patient.identifier : kindOf(s) + " n. " + s.id);
export const devLabel = (s: ISentinelRich) => kindOf(s) + " · n. " + s.id;


// perché un utente non riceve le email degli allarmi (null = le riceve)
export const mailReason = (user: IUser) => {
  if (!user.enabled) return "utente disabilitato";
  if (!user.email) return "nessuna email registrata";
  if (user.emailNotifications === false) return "notifiche email spente";
  return null;
};

export const emailsOf = (user: IUser) =>
  String(user.email ?? "")
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter(Boolean);

// chi ha in carico la persona di questo dispositivo
export const operatorsOf = (sentinel: ISentinelRich, carings: ICaring[], users: IUser[]) => {
  if (!sentinel.patient) return [];
  return carings
    .filter((c: ICaring) => c.patientId === sentinel.patient.id)
    .map((c: ICaring) => users.find((u: IUser) => u.id === c.userId))
    .filter((u: IUser | undefined) => u !== undefined) as IUser[];
};

// sensori: nome dell'app, cosa misura, valori possibili e soglia di partenza (sentinelService.createSentinel)
export interface ISensorDef {
  desc: string;
  range: [number, number];
  threshold: number;
  noAlarm?: boolean;
}

export const SENSOR_DEFS: Record<string, ISensorDef> = {
  lidarDx: { desc: "La persona prova a scendere dal lato destro del letto.", range: [15, 150], threshold: 75 },
  lidarSx: { desc: "La persona prova a scendere dal lato sinistro del letto.", range: [15, 150], threshold: 75 },
  pesogr: { desc: "Il LiDAR non vede più la persona nel letto.", range: [0, 200], threshold: 60 },
  pesogr2: { desc: "Misura il peso.", range: [0, 200], threshold: 0, noAlarm: true },
  rumore: { desc: "Un rumore forte nella stanza.", range: [0, 300], threshold: 256 },
  door: { desc: "La porta del varco si apre.", range: [0, 10], threshold: 0 },
};
export const SENSOR_ORDER = ["lidarDx", "lidarSx", "pesogr", "pesogr2", "rumore", "door"];
export const DEFAULT_SENSORS: Record<DeviceKind, string[]> = {
  Letto: ["lidarDx", "lidarSx", "pesogr", "rumore"],
  Varco: ["door"],
};

export const sensorTitle = (type: string) => getSensorIconTooltip(type);

// sensori che misurano soltanto
export const sensorGivesAlarms = (type: string) => {
  return type !== "pesogr2";
};

export const isWeightZero = (x: { type: string; threshold: any }) => x.type === "pesogr" && String(x.threshold).trim() !== "" && +x.threshold === 0;
// da controllare: peso con soglia 0 o sensore spento
export const sensorToCheck = (x: ISensor) => isWeightZero(x) || !x.enabled;

// «Scatta con distanza sotto 75»: la riga di riepilogo di un sensore
export const scattaText = (type: string, threshold: any) => {
  const v = String(threshold ?? "").trim();
  if (!sensorGivesAlarms(type)) return "Misura e basta: non dà allarmi";
  if (type === "door") return "Scatta quando la porta si apre";
  if (v === "" || isNaN(+v)) return "Soglia da correggere";
  if (type === "rumore") return "Scatta con rumore sopra " + v;
  if (type === "pesogr") return "Scatta con valore del LiDAR sotto " + v;
  return "Scatta con distanza sotto " + v;
};

// ---------------- telecamera: cameraUrl = "ip;porta;utente;password;canale[;percorso]" ----------------

export interface ICameraFields {
  ip: string;
  port: string;
  user: string;
  password: string;
  channel: string;
  // campi oltre il quinto, conservati così come sono
  path: string;
  hasPath: boolean;
}

export const parseCameraUrl = (cameraUrl: string | null | undefined): ICameraFields => {
  const parts = String(cameraUrl ?? "").split(";");
  return {
    ip: (parts[0] ?? "").trim(),
    port: (parts[1] ?? "").trim(),
    user: parts[2] ?? "",
    password: parts[3] ?? "",
    channel: (parts[4] ?? "").trim(),
    path: parts.length > 5 ? parts.slice(5).join(";") : "",
    hasPath: parts.length > 5,
  };
};

// senza indirizzo non c'è telecamera: si salva vuota
export const buildCameraUrl = (f: ICameraFields) => {
  if (f.ip.trim() === "") return "";
  const parts = [f.ip.trim(), f.port.trim(), f.user, f.password, f.channel.trim()];
  if (f.hasPath) parts.push(f.path);
  return parts.join(";");
};

// stato in tabella: mai la password
export const cameraSummary = (cameraUrl: string | null | undefined) => {
  const f = parseCameraUrl(cameraUrl);
  if (f.ip === "") return null;
  return f.ip + (f.channel !== "" ? " · canale " + f.channel : "");
};

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
// IPv4 oppure un nome (es. cam1.locale): le telecamere già salvate con un nome restano modificabili
const HOSTNAME = /^(?=.{1,253}$)[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const ipError = (ip: string) => {
  const v = ip.trim();
  if (v === "") return "Scrivi l'indirizzo IP della telecamera";
  if (/^[\d.]+$/.test(v)) return IPV4.test(v) ? null : "Indirizzo IP non valido (es. 192.168.1.20)";
  return HOSTNAME.test(v) ? null : "Indirizzo non valido (es. 192.168.1.20)";
};

export const portError = (port: string) => {
  if (port.trim() === "") return "Scrivi la porta";
  const n = Number(port.trim());
  return /^\d+$/.test(port.trim()) && n >= 1 && n <= 65535 ? null : "La porta è un numero da 1 a 65535";
};
